/*
 * Copyright © 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import { useForm } from "@/src/provider/Form/FormProvider";
import React, { ReactNode, useEffect } from "react";
import { TextField } from "@mui/material";
import { formValue } from "@/src/services/formServiceFunctions";
import _get from "lodash/get";
import { FormDataTypes } from "@/src/types/FormDataTypes";
import { TextFieldProps } from "@mui/material/TextField";
import { ValidatorType } from "@/src/types/ValidatorType";
import { hasRequiredValidator } from "@/src/services/validationRules";

export type ValidatorTextFieldProps = {
  name: string;
  label: string;
  validators?: Array<ValidatorType>;
  dependentValidationFields?: Array<string>;
  helperText?: string | null | ReactNode;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  margin?: "normal" | "dense";
  multiline?: boolean;
  rows?: number;
  InputProps?: Object;

  inputProps?: Record<string, any>;
  InputLabelProps?: Object;
} & TextFieldProps;

export default function ValidatorTextField({
  name,
  label,
  helperText,
  validators = [],
  dependentValidationFields = [],
  multiline = false,
  rows = multiline ? 6 : 1,
  fullWidth = true,
  margin = "normal",
  InputProps = {},
  inputProps = {},
  placeholder,
  InputLabelProps,
  ...rest
}: ValidatorTextFieldProps) {
  const { values, errors, setError, handleChange, handleBlur, registerValidators }: FormDataTypes = useForm();
  const error = !!_get(errors, name);
  const required = hasRequiredValidator(validators);
  useEffect(() => {
    registerValidators(name, validators, dependentValidationFields);
    return () => {
      registerValidators(name, [], []);
      setError(name, undefined);
    };
  }, [name]);

  if (!multiline && !inputProps.maxLength) {
    inputProps.maxLength = 250;
  }

  return (
    <TextField
      name={name}
      label={label}
      value={formValue(values, name)}
      error={error}
      helperText={_get(errors, name) || helperText}
      onChange={handleChange}
      onBlur={handleBlur}
      rows={rows}
      multiline={multiline}
      fullWidth={fullWidth}
      margin={margin}
      placeholder={placeholder}
      {...rest}
      slotProps={{
        input: InputProps,
        htmlInput: {
          ...inputProps,
          "aria-required": required,
        },
        inputLabel: { ...InputLabelProps, required },
        formHelperText: error ? { role: "alert" } : {},
      }}
    />
  );
}
